import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { FaArrowLeft } from "react-icons/fa";
import { Button } from "../../../components/ui";
import apiService from "../../../services/apiService";
import Loader from "../../../components/ui/Loader";
import { useToast } from "../../../context/ToastContext";

const createUploadApi = (qSetId) =>
  `/i/ims/tools/qSet/${qSetId}/upload-book-question-solution-pdf`;

const createStatusApi = (qSetId, jobId) =>
  `/i/ims/tools/qSet/${qSetId}/book-question-solution-status?jobId=${encodeURIComponent(
    jobId,
  )}`;

const createResetApi = (qSetId, jobId) =>
  `/i/ims/tools/qSet/${qSetId}/reset-book-question-solution-job?jobId=${encodeURIComponent(
    jobId,
  )}`;

const getJobStorageKey = (qSetId) =>
  `BOOK_QUESTION_ANSWER_SOLUTION_${String(qSetId || "")}`;

const getRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.records)) return response.records;
  return [];
};

const getDataObject = (response) =>
  response?.data?.data || response?.data || response || {};

const getName = (item) =>
  item?.name ||
  item?.title ||
  item?.value ||
  item?.valueAlias ||
  item?.code ||
  "";

const makeSectionKey = (name) => {
  const key = String(name || "")
    .trim()
    .toUpperCase()
    .replace(/&/g, "AND")
    .replace(/\+/g, "AND")
    .replace(/[^A-Z0-9\u0900-\u097F]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

  return key || "BOOK_SUBJECT";
};

const getStageText = (status) => {
  if (status?.message) return status.message;

  const step = Number(status?.activeStep);

  if (step < 0) return "Book question extraction failed";
  if (step === 0) return "PDF text conversion running";
  if (step === 1) return "Book content prepared";
  if (step === 2) return "AI is extracting questions";
  if (step >= 3) return "Questions saved successfully";

  return "Waiting";
};

const getProgressPercent = (status) => {
  const totalParts = Number(status?.totalParts || 0);
  const processedParts = Number(status?.processedParts || 0);

  if (totalParts > 0) {
    return Math.max(0, Math.min(100, (processedParts / totalParts) * 100));
  }

  const step = Number(status?.activeStep);

  if (step < 0) return 100;
  if (step === 0) return 20;
  if (step === 1) return 50;
  if (step === 2) return 80;
  if (step >= 3) return 100;

  return 0;
};

const oldRequest = async (url, options) => {
  if (typeof apiService.request_old === "function") {
    return apiService.request_old(url, options);
  }

  return apiService.request(url, options);
};

const UploadBookQuestionSolution = () => {
  const navigate = useNavigate();
  const { id: qSetId } = useParams();
  const timerRef = useRef(null);
  const currentJobIdRef = useRef("");
  const { showSuccess, showError, showWarning } = useToast();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      stream: "",
      subject: "",
      layoutType: "AUTO",
      translateMissingLanguage: false,
    },
  });

  const selectedStream = watch("stream");

  const [streams, setStreams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loadingStreams, setLoadingStreams] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [conversionStatus, setConversionStatus] = useState(null);

  const loadStreams = async () => {
    try {
      setLoadingStreams(true);

      const response = await oldRequest(
        "/i/ims/coption?option=STREAM&sort=seq%20ASC",
        { method: "GET" },
      );

      const rows = getRows(response);
      setStreams(rows.length ? rows : response || []);
    } catch (error) {
      console.log(error);
      showError("Stream load failed.");
    } finally {
      setLoadingStreams(false);
    }
  };

  const loadSubjects = async (streamId) => {
    if (!streamId) {
      setSubjects([]);
      return;
    }

    try {
      setLoadingSubjects(true);

      const response = await oldRequest(
        `/i/ims/subject?stream=${encodeURIComponent(streamId)}&limit=all`,
        { method: "GET" },
      );

      const rows = getRows(response);
      setSubjects(rows.length ? rows : response || []);
    } catch (error) {
      console.log(error);
      setSubjects([]);
      showError("Subject load failed.");
    } finally {
      setLoadingSubjects(false);
    }
  };

  const checkStatus = async (jobId) => {
    if (!qSetId || !jobId) return null;

    const response = await apiService.request(createStatusApi(qSetId, jobId), {
      method: "GET",
    });

    const status = getDataObject(response);

    if (status?.jobId && String(status.jobId) !== String(jobId)) {
      return null;
    }

    setConversionStatus(status);
    return status;
  };

  const pollStatus = async (requestedJobId = currentJobIdRef.current) => {
    const jobId = String(requestedJobId || "");

    if (!jobId) return;

    try {
      setPolling(true);

      const status = await checkStatus(jobId);

      if (!status) {
        setPolling(false);
        return;
      }

      const step = Number(status?.activeStep ?? 0);
      const terminal =
        step < 0 ||
        step >= 3 ||
        status?.isRunning === false ||
        status?.uploadStatus === "Failed" ||
        status?.uploadStatus === "Uploaded" ||
        status?.stage === "JOB_NOT_FOUND";

      if (terminal) {
        setPolling(false);
        currentJobIdRef.current = "";
        window.sessionStorage.removeItem(getJobStorageKey(qSetId));

        if (
          step >= 3 &&
          status?.uploadStatus !== "Failed" &&
          status?.stage !== "JOB_NOT_FOUND"
        ) {
          showSuccess(
            status?.message ||
              "Book questions, answers and solutions saved successfully.",
          );
        } else {
          showError(
            status?.message ||
              "Book question, answer and solution extraction failed.",
          );
        }

        return;
      }

      timerRef.current = setTimeout(() => pollStatus(jobId), 4000);
    } catch (error) {
      console.log(error);
      setPolling(false);
      showError(
        error?.message || "Status check failed. Reset the job and retry.",
      );
    }
  };

  useEffect(() => {
    loadStreams();
    setConversionStatus(null);
    setPolling(false);

    const savedJobId = window.sessionStorage.getItem(getJobStorageKey(qSetId));

    if (savedJobId) {
      currentJobIdRef.current = savedJobId;

      checkStatus(savedJobId)
        .then((status) => {
          if (status?.isRunning) {
            pollStatus(savedJobId);
            return;
          }

          currentJobIdRef.current = "";
          window.sessionStorage.removeItem(getJobStorageKey(qSetId));

          if (
            status?.stage === "JOB_NOT_FOUND" ||
            status?.stage === "NOT_STARTED"
          ) {
            setConversionStatus(null);
          }
        })
        .catch(() => {
          currentJobIdRef.current = "";
          window.sessionStorage.removeItem(getJobStorageKey(qSetId));
          setConversionStatus(null);
        });
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [qSetId]);

  useEffect(() => {
    loadSubjects(selectedStream);
  }, [selectedStream]);

  const resetCurrentJob = async () => {
    const jobId = String(
      currentJobIdRef.current || conversionStatus?.jobId || "",
    );

    if (!qSetId || !jobId) {
      setPolling(false);
      setConversionStatus(null);
      window.sessionStorage.removeItem(getJobStorageKey(qSetId));
      return;
    }

    try {
      setResetting(true);

      const response = await apiService.request(createResetApi(qSetId, jobId), {
        method: "POST",
      });

      const result = getDataObject(response);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      currentJobIdRef.current = "";
      window.sessionStorage.removeItem(getJobStorageKey(qSetId));
      setPolling(false);
      setConversionStatus(null);

      showSuccess(result?.message || "Stuck job reset. Upload the PDF again.");
    } catch (error) {
      showError(error?.message || "Unable to reset the stuck job.");
    } finally {
      setResetting(false);
    }
  };

  const submitBook = async (data) => {
    const file = data.file?.[0];

    if (!qSetId) {
      showError("QSet ID not found.");
      return;
    }

    if (!data.stream) {
      showWarning("Please select stream.");
      return;
    }

    if (!data.subject) {
      showWarning("Please select subject.");
      return;
    }

    if (!file) {
      showWarning("Please select PDF file.");
      return;
    }

    if (
      file.type &&
      file.type !== "application/pdf" &&
      !String(file.name || "")
        .toLowerCase()
        .endsWith(".pdf")
    ) {
      showWarning("Only PDF file is allowed.");
      return;
    }

    const selectedSubject = subjects.find(
      (item) => String(item.id || item._id) === String(data.subject),
    );

    const subjectName = getName(selectedSubject);

    const sections = [
      {
        sectionKey: makeSectionKey(subjectName),
        sectionName: subjectName,
        subject: String(data.subject),
        subjectId: String(data.subject),
        subjectName,
        questionStart: 0,
        questionEnd: 0,
      },
    ];

    const settings = {
      qSet: String(qSetId),
      stream: String(data.stream),
      subject: String(data.subject),
      subjectId: String(data.subject),
      subjectName,
      layoutType: String(data.layoutType || "AUTO"),
      translateMissingLanguage: data.translateMissingLanguage
        ? "true"
        : "false",
      sections: JSON.stringify(sections),
      subjects: JSON.stringify(sections),
    };

    try {
      setUploading(true);

      if (currentJobIdRef.current) {
        const running = await checkStatus(currentJobIdRef.current).catch(
          () => null,
        );

        if (running?.isRunning) {
          showWarning(
            "Book question, answer and solution conversion is already running.",
          );
          pollStatus(currentJobIdRef.current);
          return;
        }
      }

      const formData = new FormData();
      formData.append("pdfFile", file);

      Object.entries(settings).forEach(([key, value]) => {
        formData.append(key, value);
      });

      /*
       * Query params are also included because some Skipper/Sails multipart
       * setups expose non-file fields through query more reliably.
       * totalQuestions is deliberately not sent.
       */
      const query = new URLSearchParams(settings);

      const response = await apiService.request(
        `${createUploadApi(qSetId)}?${query.toString()}`,
        {
          method: "POST",
          body: formData,
        },
      );

      const result = getDataObject(response);
      const root = response?.data || response || {};
      const jobId = String(
        result?.jobId || root?.jobId || root?.data?.jobId || "",
      );

      if (!jobId) {
        throw new Error("Upload started but Job ID was not returned.");
      }

      currentJobIdRef.current = jobId;
      window.sessionStorage.setItem(getJobStorageKey(qSetId), jobId);

      setConversionStatus({
        ...result,
        jobId,
        isRunning: true,
        activeStep: Number(result?.activeStep ?? 0),
        message:
          result?.message ||
          "Book uploaded. Questions, answers and solutions are being extracted.",
      });

      if (result?.resumeExistingJob) {
        showWarning(result?.message || "Existing book conversion resumed.");
      } else {
        showSuccess(
          result?.message ||
            "Book question, answer and solution extraction started.",
        );
      }

      reset({
        stream: data.stream,
        subject: data.subject,
        layoutType: data.layoutType || "AUTO",
        translateMissingLanguage: Boolean(data.translateMissingLanguage),
      });

      pollStatus(jobId);
    } catch (error) {
      console.error("BOOK UPLOAD ERROR:", error);
      showError(error?.message || "Book upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const progress = getProgressPercent(conversionStatus);
  const busy = uploading || polling || resetting;

  return (
    <div className="p-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Upload Book</h1>
        </div>

        <Button
          type="button"
          variant="light"
          size="sm"
          icon={FaArrowLeft}
          onClick={() => navigate("/tools/qSet")}
        >
          Back to List
        </Button>
      </div>

      {conversionStatus && (
        <div
          className={`mb-5 rounded-lg border p-4 ${
            Number(conversionStatus.activeStep) < 0
              ? "border-red-300 bg-red-50"
              : Number(conversionStatus.activeStep) >= 3
                ? "border-green-300 bg-green-50"
                : "border-yellow-300 bg-yellow-50"
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold">{getStageText(conversionStatus)}</p>

              {conversionStatus.jobId && (
                <p className="mt-1 text-xs text-gray-600">
                  Job ID: {conversionStatus.jobId}
                </p>
              )}

              {conversionStatus.stage && (
                <p className="mt-1 text-xs text-gray-600">
                  Stage: {conversionStatus.stage}
                </p>
              )}

              {conversionStatus.updatedAt && (
                <p className="mt-1 text-xs text-gray-500">
                  Last update:{" "}
                  {new Date(conversionStatus.updatedAt).toLocaleString()}
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              {polling && <Loader />}

              {polling && conversionStatus.jobId && (
                <button
                  type="button"
                  disabled={resetting}
                  onClick={resetCurrentJob}
                  className="rounded border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  {resetting ? "Stopping..." : "Stop stuck job"}
                </button>
              )}
            </div>
          </div>

          <div className="mt-3 h-2 overflow-hidden rounded bg-gray-200">
            <div
              className="h-full bg-blue-600 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit(submitBook)}
        className="rounded-lg border bg-white p-6 shadow-sm"
      >
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium">
              Stream <span className="text-red-500">*</span>
            </label>

            <select
              {...register("stream", { required: true })}
              disabled={loadingStreams || busy}
              className="w-full rounded border p-2.5"
            >
              <option value="">
                {loadingStreams ? "Loading streams..." : "Select Stream"}
              </option>

              {streams.map((stream) => (
                <option
                  key={stream.id || stream._id}
                  value={stream.id || stream._id}
                >
                  {getName(stream)}
                </option>
              ))}
            </select>

            {errors.stream && (
              <p className="mt-1 text-xs text-red-600">Stream is required.</p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">
              Subject <span className="text-red-500">*</span>
            </label>

            <select
              {...register("subject", { required: true })}
              disabled={!selectedStream || loadingSubjects || busy}
              className="w-full rounded border p-2.5"
            >
              <option value="">
                {!selectedStream
                  ? "Select stream first"
                  : loadingSubjects
                    ? "Loading subjects..."
                    : "Select Subject"}
              </option>

              {subjects.map((subject) => (
                <option
                  key={subject.id || subject._id}
                  value={subject.id || subject._id}
                >
                  {getName(subject)}
                </option>
              ))}
            </select>

            {errors.subject && (
              <p className="mt-1 text-xs text-red-600">Subject is required.</p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">
              PDF Layout / Format <span className="text-red-500">*</span>
            </label>

            <select
              {...register("layoutType", { required: true })}
              disabled={busy}
              className="w-full rounded border p-2.5"
            >
              <option value="AUTO">Auto detect (recommended)</option>
              <option value="TWO_COLUMN">Two Column / Half-Half</option>
              <option value="ONE_COLUMN">One Column</option>
              <option value="QUESTION_GRID">Question Grid</option>
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">
              Missing Language Translation
            </label>

            <label className="flex min-h-[43px] items-center gap-2 rounded border px-3">
              <input
                type="checkbox"
                disabled={busy}
                {...register("translateMissingLanguage")}
              />

              <span className="text-sm">
                Translate missing question and solution language
              </span>
            </label>
          </div>

          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium">
              Select PDF File <span className="text-red-500">*</span>
            </label>

            <input
              type="file"
              accept="application/pdf,.pdf"
              disabled={busy}
              {...register("file", { required: true })}
              className="w-full rounded border p-2.5 text-sm"
            />

            {errors.file && (
              <p className="mt-1 text-xs text-red-600">PDF file is required.</p>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button type="submit" disabled={busy}>
            {uploading
              ? "Uploading..."
              : polling
                ? "Extracting Questions & Solutions..."
                : "Upload Book Questions & Solutions"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UploadBookQuestionSolution;
